import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16 } from '@onefootprint/icons';
import type { ApiNodeDataCommonExpression } from '@onefootprint/request-types/dashboard';
import { Divider, Form, InlineAlert, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useEffect } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExpressionEditor } from '@/components/expression-editor';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import { isTypeable, useValidateExpressions } from '@/components/playbook-builder/hooks/use-validate-expressions';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../base-node';
import { CelNodeFormInstructions, CelNodeFormSubtitle } from '../../shared/cel-node-form-instructions';
import ExpressionTypeHint from '../../shared/expression-type-hint';
import { getAutocompleteWithPriorDataKeys, getDefaultValues } from '../utils';
type ExpressionNodeFormProps = {
  node: NodeProps<ApiNodeDataCommonExpression>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
};
type ExpressionFormData = {
  name: string;
  programs: {
    dataKey: string;
    expression: string;
  }[];
};
export const ExpressionNodeForm = t0 => {
  const $ = _c(89);
  const {
    node,
    mutation,
    canEdit
  } = t0;
  const {
    toggle
  } = useDirty();
  const t1 = !canEdit;
  const t2 = node.data.name;
  let t3;
  if ($[0] !== node.data.expressions.programs) {
    t3 = getDefaultValues(node.data.expressions.programs);
    $[0] = node.data.expressions.programs;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== node.data.name || $[3] !== t3) {
    t4 = {
      name: t2,
      programs: t3
    };
    $[2] = node.data.name;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t4) {
    t5 = {
      disabled: t1,
      defaultValues: t4
    };
    $[5] = t1;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const form = useForm(t5);
  const config = useGetNodeConfigContext(node.id, "expression");
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      keyPrefix: "expressionNode"
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    t
  } = useTranslation("playbook-builder", t6);
  let t7;
  if ($[9] !== form.control) {
    t7 = {
      control: form.control,
      name: "programs"
    };
    $[9] = form.control;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t7);
  let t8;
  if ($[11] !== form.control) {
    t8 = {
      control: form.control,
      name: "programs"
    };
    $[11] = form.control;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const watchedPrograms = useWatch(t8);
  let t9;
  if ($[13] !== watchedPrograms) {
    t9 = watchedPrograms ?? [];
    $[13] = watchedPrograms;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const typesByDataKey = useValidateExpressions(node.id, t9);
  let t10;
  if ($[15] !== form.formState.isDirty || $[16] !== toggle) {
    t10 = () => {
      toggle("expression-form ", form.formState.isDirty);
    };
    $[15] = form.formState.isDirty;
    $[16] = toggle;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== form.formState.isDirty) {
    t11 = [form.formState.isDirty];
    $[18] = form.formState.isDirty;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  if ($[20] !== append) {
    t12 = () => {
      append({
        dataKey: "",
        expression: ""
      }, {
        shouldFocus: false
      });
    };
    $[20] = append;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const onAddExpression = t12;
  let t13;
  if ($[22] !== mutation || $[23] !== node.id) {
    t13 = async data => {
      ;
      try {
        await mutation.mutateAsync({
          kind: "update_node",
          nodeId: node.id,
          nodeData: {
            name: data.name,
            kind: "common_expression",
            expressions: {
              programs: data.programs.map(_temp)
            }
          }
        });
      } catch (t14) {
        const error = t14;
        console.error("Error updating playbook session:", error);
      }
    };
    $[22] = mutation;
    $[23] = node.id;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const handleFormSubmit = t13;
  let t14;
  if ($[25] !== form || $[26] !== handleFormSubmit) {
    t14 = form.handleSubmit(handleFormSubmit);
    $[25] = form;
    $[26] = handleFormSubmit;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t) {
    t15 = t(_temp2);
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t) {
    t16 = t(_temp3);
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t) {
    t17 = t(_temp4);
    $[32] = t;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t17) {
    t18 = {
      required: t17
    };
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t15 || $[37] !== t16 || $[38] !== t18) {
    t19 = <Form.Input label={t15} name="name" placeholder={t16} size="compact" rules={t18} />;
    $[36] = t15;
    $[37] = t16;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <CelNodeFormSubtitle i18nKey={_temp5} />;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t) {
    t21 = t(_temp6);
    $[41] = t;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t) {
    t22 = t(_temp7);
    $[43] = t;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t21 || $[46] !== t22) {
    t23 = <CelNodeFormInstructions subtitle={t20} celSubtitle={t21} instructionsLabel={t22} />;
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t19 || $[49] !== t23) {
    t24 = <div className="bg-secondary p-4 flex flex-col gap-3">{t19}{t23}</div>;
    $[48] = t19;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Divider />;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t24) {
    t26 = <section>{t24}{t25}</section>;
    $[52] = t24;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== canEdit || $[55] !== config?.autocomplete || $[56] !== fields || $[57] !== form.control || $[58] !== form.formState.errors || $[59] !== remove || $[60] !== t || $[61] !== typesByDataKey || $[62] !== watchedPrograms) {
    let t28;
    if ($[64] !== canEdit || $[65] !== config?.autocomplete || $[66] !== fields.length || $[67] !== form.control || $[68] !== form.formState.errors || $[69] !== remove || $[70] !== t || $[71] !== typesByDataKey || $[72] !== watchedPrograms) {
      t28 = (field, index) => {
        const fieldError = form.formState.errors.programs?.[index];
        const priorDataKeys = (watchedPrograms ?? []).slice(0, index).map(_temp8);
        const autocompleteItems = getAutocompleteWithPriorDataKeys(config?.autocomplete, priorDataKeys, typesByDataKey);
        const program_0 = watchedPrograms?.[index];
        const expressionType = typesByDataKey.get(program_0?.dataKey ?? "");
        const problem = expressionType?.problems[0];
        return <section key={field.id} className="flex flex-col border border-tertiary rounded p-4 gap-6 mx-4"><div className="flex flex-col gap-2 "><div className="flex justify-between items-center gap-2"><div className="flex items-center gap-2"><label htmlFor="programs" className="text-body-2">{t(_temp9)}</label><ExpressionTypeHint expressionType={expressionType} problem={problem} isTypeable={isTypeable(program_0 ?? {})} /></div>{fields.length > 1 && <LinkButton destructive={true} type="button" onClick={() => remove(index)} disabled={!canEdit} variant="label-3" className="flex-shrink-0">{t(_temp10)}</LinkButton>}</div><div className={cx("flex justify-between items-center gap-2")}><Controller name={`programs.${index}.expression`} control={form.control} rules={{
                required: t(_temp11)
              }} render={t29 => {
                const {
                  field: field_0
                } = t29;
                return <div className={cx("flex flex-col gap-2 w-full relative")}><ExpressionEditor {...field_0} variant="textarea" autocompleteItems={autocompleteItems} disabled={!canEdit || field_0.disabled} placeholder={t(_temp12)} hasError={!!fieldError?.expression || problem?.severity === "error"} />{fieldError?.expression ? <InlineAlert variant="error">{fieldError.expression.message}</InlineAlert> : problem?.severity === "error" && <InlineAlert variant="error">{problem.message}</InlineAlert>}</div>;
              }} /></div></div><Form.Input name={`programs.${index}.dataKey`} placeholder={t(_temp13)} size="compact" rules={{
            required: t(_temp14)
          }} label={t(_temp15)} addon="data." /></section>;
      };
      $[64] = canEdit;
      $[65] = config?.autocomplete;
      $[66] = fields.length;
      $[67] = form.control;
      $[68] = form.formState.errors;
      $[69] = remove;
      $[70] = t;
      $[71] = typesByDataKey;
      $[72] = watchedPrograms;
      $[73] = t28;
    } else {
      t28 = $[73];
    }
    t27 = fields.map(t28);
    $[54] = canEdit;
    $[55] = config?.autocomplete;
    $[56] = fields;
    $[57] = form.control;
    $[58] = form.formState.errors;
    $[59] = remove;
    $[60] = t;
    $[61] = typesByDataKey;
    $[62] = watchedPrograms;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Divider />;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  const t29 = !canEdit;
  let t30;
  if ($[75] !== t) {
    t30 = t(_temp16);
    $[75] = t;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== onAddExpression || $[78] !== t29 || $[79] !== t30) {
    t31 = <section className="px-4 pb-4 flex flex-col gap-4">{t28}<LinkButton type="button" onClick={onAddExpression} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={t29}>{t30}</LinkButton></section>;
    $[77] = onAddExpression;
    $[78] = t29;
    $[79] = t30;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  let t32;
  if ($[81] !== t14 || $[82] !== t26 || $[83] !== t27 || $[84] !== t31) {
    t32 = <form id="expression-form" onSubmit={t14} className="flex flex-col gap-5 border border-tertiary rounded">{t26}{t27}{t31}</form>;
    $[81] = t14;
    $[82] = t26;
    $[83] = t27;
    $[84] = t31;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  let t33;
  if ($[86] !== form || $[87] !== t32) {
    t33 = <FormProvider {...form}>{t32}</FormProvider>;
    $[86] = form;
    $[87] = t32;
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  return t33;
};
function _temp(program) {
  return {
    dataKey: program.dataKey,
    expression: program.expression
  };
}
function _temp2($) {
  return $.expressionNameLabel;
}
function _temp3($_0) {
  return $_0.expressionNamePlaceholder;
}
function _temp4($_1) {
  return $_1.expressionNameRequired;
}
function _temp5($_2) {
  return $_2.expressionNode.subtitle;
}
function _temp6($_3) {
  return $_3.celSubtitle;
}
function _temp7($_4) {
  return $_4.instructions;
}
function _temp8(p) {
  return p.dataKey ?? "";
}
function _temp9($_5) {
  return $_5.expressionLabel;
}
function _temp10($_6) {
  return $_6.delete;
}
function _temp11($_7) {
  return $_7.expressionRequired;
}
function _temp12($_8) {
  return $_8.expressionPlaceholder;
}
function _temp13($_9) {
  return $_9.assignToPlaceholder;
}
function _temp14($_10) {
  return $_10.assignToRequired;
}
function _temp15($_11) {
  return $_11.assignToLabel;
}
function _temp16($_12) {
  return $_12.addNew;
}